import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { TimeZoneService } from '../../../../../../common/time-zone/time-zone.service';
import {
  parseDayString,
  toDayString,
} from '../../../../../../shared/helper/datehelper';
import {
  ALL_SELECTION_ID,
  normalizeAllSelection,
  withAllOption,
  withoutAllOption,
} from '../../../../../../shared/helper/selectionhelper';
import {
  AttendanceRegularityQueryModel,
  AttendanceUsersQueryModel,
} from '../../../../../../shared/store/attendance/user-work-attendance/types';
import { UserWorkAttendanceService } from '../../../../../../shared/store/attendance/user-work-attendance/user-work-attendance.service';
import { GroupService } from '../../../../../../shared/store/group/group.service';
import { SalesTeamService } from '../../../../../../shared/store/sales-team/sales-team.service';
import { ListItem } from '../../../../../../shared/store/typeahead/types';
import { ViewService } from '../../../../../../shared/store/view/views.service';
import { View } from '../../../../../../shared/store/view/types';
import { ATTENDANCE_DEFAULT_GROUP_IDS } from '../../shared/attendance-dashboard.types';

/**
 * Owns the whole selection of the team dashboard — day, offices, groups, salespeople, former
 * employees — and emits it as the query the parent sends.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-work-attendance-dashboard-subheader',
  standalone: false,
  templateUrl: './user-work-attendance-dashboard-subheader.component.html',
  styleUrl: './user-work-attendance-dashboard-subheader.component.scss',
})
export class UserWorkAttendanceDashboardSubheaderComponent
  implements OnInit, OnDestroy
{
  @Output() queryChange = new EventEmitter<AttendanceRegularityQueryModel>();

  view$: Observable<View>;

  salesTeams$: Observable<ListItem[]>;
  groups$: Observable<ListItem[]>;
  users$: Observable<ListItem[]>;

  day = '';
  salesTeamIds: number[] = [ALL_SELECTION_ID];
  groupIds: number[] = [...ATTENDANCE_DEFAULT_GROUP_IDS];
  userIds: number[] = [ALL_SELECTION_ID];
  includeFormerEmployees = false;

  /** Last day that can hold data: the date field and the right chevron stop there. */
  readonly maxDay: string;

  constructor(
    private readonly viewService: ViewService,
    private readonly translateService: TranslateService,
    private readonly salesTeamService: SalesTeamService,
    private readonly groupService: GroupService,
    private readonly userWorkAttendanceService: UserWorkAttendanceService,
    private readonly timeZoneService: TimeZoneService,
    private readonly breadcrumbService: BreadcrumbService,
  ) {
    // Today in the site's time zone, as the API reads the days.
    this.maxDay = toDayString(
      this.lastWorkingDay(
        parseDayString(
          this.timeZoneService.fromUtcToLocal(new Date(), 'yyyy-MM-dd'),
        ),
      ),
    );

    this.view$ = this.viewService.getViews.value$;
    // stream, not instant: translations may land after the list, and the "All" label must follow.
    this.salesTeams$ = combineLatest([
      this.salesTeamService.getActiveSalesTeamsSelector.value$.pipe(
        filter((x) => !!x),
      ),
      this.translateService.stream(
        'DASHBOARD_LBL_SALESTEAM_ALL',
      ) as Observable<string>,
    ]).pipe(map(([x, allLabel]) => withAllOption(x, allLabel)));
    this.groups$ = combineLatest([
      this.groupService.getListItem.value$.pipe(filter((x) => !!x)),
      this.translateService.stream(
        'ATTENDANCE_LBL_GROUP_ALL',
      ) as Observable<string>,
    ]).pipe(map(([x, allLabel]) => withAllOption(x, allLabel)));
    this.users$ = combineLatest([
      this.userWorkAttendanceService.getUsers.value$.pipe(filter((x) => !!x)),
      this.translateService.stream(
        'DASHBOARD_LBL_SALESPERSON_ALL',
      ) as Observable<string>,
    ]).pipe(map(([x, allLabel]) => withAllOption(x, allLabel)));
  }

  ngOnInit(): void {
    this.view$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@userWorkAttendanceDashboard', x.title);
      });

    // A picked salesperson the new filters no longer offer is dropped, so the picker never shows a
    // selection the rows ignore.
    this.users$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((users) => this.retainOfferedUsers(users));

    this.salesTeamService.getActiveSalesTeamsSelector.call([]);
    this.groupService.getListItem.call();

    this.day = this.maxDay;
    this.onPopulationChange();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.set('@userWorkAttendanceDashboard', '');
    this.userWorkAttendanceService.getUsers.reset();
    this.salesTeamService.getActiveSalesTeamsSelector.reset();
    this.groupService.getListItem.reset();
  }

  get canStepForward(): boolean {
    return !!this.day && this.day < this.maxDay;
  }

  onDayChange(day: string): void {
    if (!day) return;

    this.day = day > this.maxDay ? this.maxDay : day;
    this.onPopulationChange();
  }

  /** Steps to the neighbouring weekday: the back returns no weekend day. */
  stepDay(offset: number): void {
    const date = parseDayString(this.day);

    do {
      date.setDate(date.getDate() + offset);
    } while (isWeekend(date));

    this.onDayChange(toDayString(date));
  }

  onSalesTeamIdsChange(): void {
    this.salesTeamIds = normalizeAllSelection(this.salesTeamIds);
    this.onPopulationChange();
  }

  onGroupIdsChange(): void {
    this.groupIds = normalizeAllSelection(this.groupIds);
    this.onPopulationChange();
  }

  onUserIdsChange(): void {
    this.userIds = normalizeAllSelection(this.userIds);
    this.queryChange.emit(this.buildQuery());
  }

  /** Day, office, group or former employees moved: the picker reloads, and so do the rows. */
  onPopulationChange(): void {
    this.userWorkAttendanceService.getUsers.call(this.buildUsersQuery());
    this.queryChange.emit(this.buildQuery());
  }

  private retainOfferedUsers(users: ListItem[]): void {
    const offeredIds = new Set(users.map((x) => x.id));
    const kept = this.userIds.filter(
      (id) => id === ALL_SELECTION_ID || offeredIds.has(id),
    );

    if (kept.length === this.userIds.length) return;

    this.userIds = normalizeAllSelection(kept);
    this.queryChange.emit(this.buildQuery());
  }

  private buildUsersQuery(): AttendanceUsersQueryModel {
    return {
      from: this.day,
      to: this.day,
      salesTeamIds: withoutAllOption(this.salesTeamIds),
      groupIds: withoutAllOption(this.groupIds),
      includeFormerEmployees: this.includeFormerEmployees,
    };
  }

  private buildQuery(): AttendanceRegularityQueryModel {
    return {
      ...this.buildUsersQuery(),
      userIds: withoutAllOption(this.userIds),
    };
  }

  private lastWorkingDay(from: Date): Date {
    const date = new Date(from);

    while (isWeekend(date)) date.setDate(date.getDate() - 1);

    return date;
  }
}

function isWeekend(date: Date): boolean {
  return date.getDay() === 0 || date.getDay() === 6;
}
